/* eslint-disable operator-linebreak */
import { FC, useState } from 'react';
import moment from 'moment';
import { DatePicker } from 'antd';

import ORScheduler from './ORScheduler';
import USHolidaysAPI from './UsHolidays';
import { FormLabel } from '../../components/FormLabel';
import { OR, PeriodSchedule } from './ScheduleTypes';
import ORScheduleSelector from './ORScheduleSelector';
import { useSchedule } from './scheduleState/ScheduleContext';
import { ACTION_TYPES } from './scheduleState/ScheduleReducer';

const ORSchedulerSection: FC<any> = ({
  ORsList,
  periodId,
  periodSchedule,
  selectedOR,
  setSelectedOR,
  hospitalDispatch,
}) => {
  return (
    <div className="grid grid-cols-[30%_40%_30%]">
      <div className="">
        <ORScheduleSelector
          ORsList={ORsList}
          periodSchedule={periodSchedule}
          hospitalDispatch={hospitalDispatch}
          selectedOR={selectedOR}
          setSelectedOR={setSelectedOR}
        />
      </div>

      <div className="">
        {selectedOR || periodSchedule.applyToAllORs ? (
          <ORScheduler
            periodId={periodId}
            OR={selectedOR}
            schedule={periodSchedule.schedule[selectedOR || 'AllORs']}
            hospitalDispatch={hospitalDispatch}
          />
        ) : (
          <div>Select an OR or apply to All</div>
        )}
      </div>
    </div>
  );
};

interface ScheduleFormProps {
  periodId: string;
  ORsList: OR[];
  setActivePeriodIdEditing: (periodId: string | null) => void;
}

const SchedulerForm: FC<ScheduleFormProps> = ({ periodId, ORsList, setActivePeriodIdEditing }) => {
  const [selectedOR, setSelectedOR] = useState<string | null>('AllORs');
  const [activeTab, setActiveTab] = useState(0);
  const { hospitalSchedule, hospitalDispatch } = useSchedule();
  const periodSchedule =
    hospitalSchedule.find((period) => period.id === periodId) ?? ({} as PeriodSchedule);

  const tabs = [
    {
      name: 'OR Schedule',
      content: (
        <ORSchedulerSection
          ORsList={ORsList}
          periodId={periodId}
          periodSchedule={periodSchedule}
          selectedOR={selectedOR}
          setSelectedOR={setSelectedOR}
          hospitalDispatch={hospitalDispatch}
        />
      ),
    },
    { name: 'Holidays', content: <USHolidaysAPI periodId={periodId} /> },
  ];

  return (
    <div className="bg-gray-50">
      <div className="w-full rounded-lg border-1 border-gray-200">
        <div className="w-full flex justify-start items-center p-1 py-2 border-gray-200 whitespace-nowrap">
          <FormLabel className="font-bold text-lg bg-gray-200 p-1 my-1 pl-3 rounded-md mr-3">
            Period Name
            <input
              name="PeriodName"
              className="border-none focus:ring-0 ml-3 sm:w-32 xl:w-fit text-xl h-12"
              type="text"
              value={periodSchedule.name}
              onChange={(e) => {
                hospitalDispatch({
                  type: ACTION_TYPES.UPDATE_PERIOD_PROPERTY,
                  payload: {
                    periodId,
                    property: 'name',
                    updatedValue: e.target.value,
                  },
                });
              }}
            />
          </FormLabel>
          <FormLabel className="font-bold text-lg bg-gray-200 p-1 my-1 pl-3 rounded-md mr-3">
            Start Date
            <DatePicker
              name="PeriodStartDate"
              allowClear={false}
              className="border-none focus:ring-0 ml-3 sm:w-32 xl:w-fit text-xl h-12"
              value={moment(periodSchedule.startDate)}
              format="MM/DD/YYYY"
              disabledDate={(current) => current.isAfter(periodSchedule.endDate)}
              onChange={(date) => {
                hospitalDispatch({
                  type: ACTION_TYPES.UPDATE_PERIOD_PROPERTY,
                  payload: {
                    periodId,
                    property: 'startDate',
                    updatedValue: date ? date.format('YYYY-MM-DD') : '',
                  },
                });
              }}
            />
          </FormLabel>
          <FormLabel className="font-bold text-lg bg-gray-200 p-1 my-1 pl-3 rounded-md mr-3">
            End Date
            <DatePicker
              name="PeriodEndDate"
              allowClear={false}
              className="border-none focus:ring-0 ml-3 sm:w-32 xl:w-fit text-xl h-12"
              value={moment(periodSchedule.endDate)}
              format="MM/DD/YYYY"
              disabledDate={(current) => current.isBefore(periodSchedule.startDate)}
              onChange={(date) => {
                hospitalDispatch({
                  type: ACTION_TYPES.UPDATE_PERIOD_PROPERTY,
                  payload: {
                    periodId,
                    property: 'endDate',
                    updatedValue: date ? date.format('YYYY-MM-DD') : '',
                  },
                });
              }}
            />
          </FormLabel>
          <span
            className="ml-auto my-1 px-4 py-1 mr-3 text-xlg font-bold inline-block rounded-md w-32 bg-persianBlue hover:bg-blue text-white hover:text-gray-100 cursor-pointer transition-all duration-300 whitespace-nowrap align-center text-center"
            onClick={() => setActivePeriodIdEditing(null)}
          >
            Preview
          </span>
        </div>
      </div>
      <div className="h-3 bg-white"></div>
      <div className="w-full grid gap-2 p-5 rounded-lg border-1 border-gray-200">
        <div className="w-full mx-auto">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-5 ">
              {tabs.map((tab, index) => (
                <span
                  key={tab.name}
                  onClick={() => setActiveTab(index)}
                  className={`
              pt-4 pb-2 px-1 text-center border-b-2 text-xl font-bold transition-colors duration-300 cursor-pointer
              ${
                activeTab === index
                  ? 'border-blue text-blue font-bold'
                  : 'border-none text-gray-500 hover:text-gray-700 hover:border-y-indigo-200 font-bold'
              }
            `}
                >
                  {tab.name}
                </span>
              ))}
            </nav>
          </div>

          <div className="mt-8">{tabs[activeTab].content}</div>
          <div className="flex justify-end">
            <span
              className="ml-auto mr-1"
              onClick={() =>
                hospitalDispatch({
                  type: ACTION_TYPES.REMOVE_PERIOD_SCHEDULE,
                  payload: {
                    periodId,
                  },
                })
              }
            >
              <span className="group my-1 w-40 px-4 py-2 mr-1 text-lg font-bold border-2 inline-block hover:pl-3 rounded-md text-gray-500 hover:bg-red-700 hover:text-white cursor-pointer transition-all duration-300 whitespace-nowrap">
                Remove Period <span className="hidden group-hover:inline-block">?</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulerForm;
